import type { FoundryV2TraceabilityRecord } from "../api/foundryV2Client";

function statusTone(status: "ready" | "watch" | "blocked"): string {
  switch (status) {
    case "ready":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "watch":
      return "border-amber-200 bg-amber-50 text-amber-700";
    default:
      return "border-red-200 bg-red-50 text-red-700";
  }
}

function nodeTone(status: "ready" | "watch" | "missing" | "blocked"): string {
  switch (status) {
    case "ready":
      return "border-emerald-200 bg-emerald-50 text-emerald-800";
    case "watch":
      return "border-amber-200 bg-amber-50 text-amber-800";
    case "blocked":
      return "border-red-200 bg-red-50 text-red-800";
    default:
      return "border-slate-200 bg-slate-100 text-slate-600";
  }
}

type Props = {
  traceability: FoundryV2TraceabilityRecord | null;
  isLoading: boolean;
  error: string | null;
};

export function FoundryTraceabilityPanel({
  traceability,
  isLoading,
  error,
}: Props): JSX.Element {
  const deliveryGraph = traceability?.deliveryGraph ?? null;
  const summary = deliveryGraph?.summary ?? traceability?.traceability.summary;
  const nodes = deliveryGraph?.nodes ?? traceability?.traceability.nodes ?? [];
  const blockers = deliveryGraph?.blockers ?? traceability?.traceability.blockers ?? [];
  const founderView = deliveryGraph?.readableViews.founder ?? traceability?.traceability.readableViews.founder ?? [];
  const tacticalView = deliveryGraph?.readableViews.tactical ?? traceability?.traceability.readableViews.tactical ?? [];
  const graphMetrics = deliveryGraph?.summary ?? null;
  const blockingBlockers = blockers.filter((blocker) => blocker.severity === "blocking");
  const watchBlockers = blockers.filter((blocker) => blocker.severity === "watch");

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f6f9ff_100%)] p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]"
      data-testid="foundry-traceability-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
            Delivery Graph v1
          </div>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">
            The real parent chain from intent to release, with promotability and proof gaps made explicit
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            This graph makes parentage explicit across Atlas signal, spec validator, decisions, LEC v2, brick locking,
            deterministic assembly, Jarvis execution, runtime release, release bundle, attestation, and feedback. If the chain breaks,
            promotion must stop before the next surface claims green.
          </p>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {isLoading && !traceability ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Loading traceability chain...
        </div>
      ) : null}

      {traceability && summary ? (
        <>
          <div className="mt-5 grid gap-3 xl:grid-cols-[1.1fr_0.8fr_0.8fr_0.9fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex items-center gap-2">
                <span
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusTone(
                    summary.status,
                  )}`}
                >
                  {summary.status}
                </span>
                <span className="text-xs text-slate-500">
                  {summary.readyNodeCount}/{summary.totalNodeCount} nodes green
                </span>
              </div>
              <div className="mt-3 text-sm text-slate-700">{summary.headline}</div>
              <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-3">
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {blockingBlockers.length} blocking
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  {watchBlockers.length} under watch
                </div>
                <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                  Promotion {summary.canPromote ? "allowed" : "blocked"}
                </div>
              </div>
              {graphMetrics ? (
                <div className="mt-3 grid gap-2 text-xs text-slate-600 md:grid-cols-3">
                  <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                    coverage {graphMetrics.coverageScore}
                  </div>
                  <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                    {graphMetrics.blockedEdgeCount} blocked edge(s)
                  </div>
                  <div className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                    {graphMetrics.orphanNodeCount} orphan node(s)
                  </div>
                </div>
              ) : null}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Founder view</div>
              <ul className="mt-3 space-y-2 text-xs text-slate-600">
                {founderView.map((item) => (
                  <li key={item} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Tactical view</div>
              <ul className="mt-3 space-y-2 text-xs text-slate-600">
                {tacticalView.map((item) => (
                  <li key={item} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Artifacts</div>
              <div className="mt-3 space-y-2 text-xs">
                <a className="block font-medium text-blue-700 underline" href={traceability.artifactUrls.deliveryGraph}>
                  delivery_graph.v1.json
                </a>
                <a className="block font-medium text-blue-700 underline" href={traceability.artifactUrls.traceabilityContract}>
                  traceability_contract.v2.json
                </a>
                <a className="block font-medium text-blue-700 underline" href={traceability.artifactUrls.traceabilityGraph}>
                  traceability_graph.sample.json
                </a>
                <a className="block font-medium text-blue-700 underline" href={traceability.artifactUrls.traceabilityBlockers}>
                  traceability_blockers.sample.json
                </a>
              </div>
            </div>
          </div>

          <div className="mt-5 grid gap-4 xl:grid-cols-[1.35fr_0.95fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Parent chain
                </div>
                <div className="text-xs text-slate-500">Readable outside the repo</div>
              </div>
              <div className="mt-3 grid gap-3 lg:grid-cols-2">
                {nodes.map((node) => (
                  <div
                    key={node.id}
                    className={`rounded-2xl border px-4 py-3 ${nodeTone(node.status)}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold">{node.title}</div>
                      <span className="rounded-full border border-current/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                        {node.status}
                      </span>
                    </div>
                    <div className="mt-1 text-[11px] uppercase tracking-[0.14em] opacity-70">{node.type}</div>
                    {node.note ? <div className="mt-2 text-xs opacity-90">{node.note}</div> : null}
                    <div className="mt-3 flex flex-wrap gap-2 text-[11px] opacity-80">
                      {node.parentIds.map((parentId) => (
                        <span key={`${node.id}-${parentId}`} className="rounded-full border border-current/15 px-2 py-1">
                          parent: {parentId}
                        </span>
                      ))}
                      {node.childIds?.map((childId) => (
                        <span key={`${node.id}-${childId}`} className="rounded-full border border-current/15 px-2 py-1">
                          child: {childId}
                        </span>
                      ))}
                    </div>
                    {node.artifactUrl ? (
                      <a className="mt-3 block text-xs font-semibold underline" href={node.artifactUrl}>
                        Open artifact
                      </a>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Promotion blockers
                </div>
                <div className="text-xs text-slate-500">
                  {blockingBlockers.length} blocking · {watchBlockers.length} watch
                </div>
              </div>
              <div className="mt-3 space-y-3 text-xs text-slate-600">
                {blockers.map((blocker) => (
                  <div
                    key={blocker.id}
                    className={`rounded-xl border px-3 py-3 ${
                      blocker.severity === "blocking"
                        ? "border-red-200 bg-red-50 text-red-800"
                        : "border-amber-200 bg-amber-50 text-amber-800"
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold">{blocker.id}</div>
                      <span className="rounded-full border border-current/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                        {blocker.effect === "block_promotion" ? "blocks promotion" : "watch"}
                      </span>
                    </div>
                    <div className="mt-2">{blocker.reason}</div>
                    {blocker.nodeId ? <div className="mt-2 opacity-80">Node: {blocker.nodeId}</div> : null}
                    {"edgeId" in blocker && blocker.edgeId ? <div className="mt-2 opacity-80">Edge: {blocker.edgeId}</div> : null}
                  </div>
                ))}
                {!blockers.length ? (
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-3 text-emerald-800">
                    No blocker remains in the delivery graph.
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
